import React from 'react';
import { FileEdit, CheckCircle2, Archive, XCircle, Lock, FlaskConical } from 'lucide-react';

/**
 * Lifecycle status as a segmented bar rather than a dropdown.
 *
 * These states are a PROGRESSION (draft -> active -> retired), and the
 * consequence of each is what actually matters — so the bar reads left to
 * right and always spells out what the selected state does. A dropdown hides
 * both the shape and the consequence behind a click.
 *
 * Shared by the device editor and the bundle builder so status looks and
 * behaves the same everywhere in Device Configurations.
 */

export type StatusTone = 'neutral' | 'info' | 'positive' | 'warning' | 'danger';

export interface StatusOption {
  value: string;
  label: string;
  /** What choosing this actually does. Shown under the bar when selected. */
  description: string;
  tone: StatusTone;
  /** When set, the segment cannot be chosen and this explains why. */
  lockedReason?: string;
  /** Overrides the tone's icon, for a state the tone alone does not describe. */
  icon?: React.FC<any>;
}

const ICON: Record<StatusTone, React.FC<any>> = {
  neutral: FileEdit,
  info: FlaskConical,
  positive: CheckCircle2,
  warning: Archive,
  danger: XCircle,
};

// Selected: filled. Unselected: quiet outline. Kept as whole class strings so
// Tailwind's scanner can see them — do not build these by interpolation.
const SELECTED: Record<StatusTone, string> = {
  neutral: 'bg-gray-600 text-white border-gray-600',
  info: 'bg-sky-600 text-white border-sky-600',
  positive: 'bg-emerald-600 text-white border-emerald-600',
  warning: 'bg-amber-500 text-white border-amber-500',
  danger: 'bg-red-600 text-white border-red-600',
};

const PANEL: Record<StatusTone, string> = {
  neutral: 'bg-gray-50 border-gray-200 text-gray-700',
  info: 'bg-sky-50 border-sky-200 text-sky-800',
  positive: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
  danger: 'bg-red-50 border-red-200 text-red-800',
};

interface Props {
  options: StatusOption[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  /**
   * Shown in place of a description when `value` is not one of the options —
   * i.e. the row is in a state this bar deliberately does not offer (a draft).
   * Without it the bar would describe a state nobody picked.
   */
  unmatchedHint?: string;
}

const StatusBar: React.FC<Props> = ({ options, value, onChange, label, unmatchedHint }) => {
  const match = options.find((o) => o.value === value);
  const selected = match || options[0];

  return (
    <div>
      {label && (
        <label className="mb-1.5 block text-xs font-semibold text-gray-700">{label}</label>
      )}

      <div
        role="radiogroup"
        aria-label={label || 'Status'}
        className="flex overflow-hidden rounded-xl border border-gray-200 bg-gray-50 p-1"
      >
        {options.map((o) => {
          const on = o.value === value;
          const Icon = o.icon || ICON[o.tone];
          const locked = !!o.lockedReason;
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={locked}
              title={o.lockedReason || o.description}
              onClick={() => !locked && onChange(o.value)}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs font-semibold transition
                ${on ? SELECTED[o.tone] : 'border-transparent text-gray-500 hover:bg-white hover:text-gray-700'}
                ${locked ? 'cursor-not-allowed opacity-40' : ''}`}
            >
              {locked ? <Lock size={12} /> : <Icon size={13} />}
              <span className="truncate">{o.label}</span>
            </button>
          );
        })}
      </div>

      {/* The consequence is the point — always visible, never behind a hover. */}
      <div
        className={`mt-2 rounded-lg border px-3 py-2 text-[11px] leading-relaxed ${
          !match && unmatchedHint ? PANEL.neutral : PANEL[selected.tone]
        }`}
      >
        {!match && unmatchedHint ? unmatchedHint : selected.description}
      </div>
    </div>
  );
};

export default StatusBar;

/**
 * Device lifecycle — shared so the wording cannot drift between screens.
 *
 * DRAFT IS NOT HERE ON PURPOSE. Draft is not a lifecycle an operator picks: it
 * is the state "Save draft" writes and "Publish device" clears, and both of
 * those buttons sit in the editor's top bar. Offering it here too meant one
 * fact with two controls that could disagree. The slot now carries `pilot`,
 * which is a genuine lifecycle choice and had nowhere else to live.
 *
 * A row already saved as a draft matches none of these, and the editor says so
 * through `unmatchedHint` instead of adding a segment for it.
 */
export const DEVICE_STATUS_OPTIONS: StatusOption[] = [
  {
    value: 'pilot', label: 'Pilot', tone: 'info',
    description: 'In trial with selected tenants. Real and deployable, but not yet a general release — note that nothing blocks it from bundles or deals today.',
  },
  {
    value: 'active', label: 'Active', tone: 'positive',
    description: 'Sellable. Appears in bundles, deals and the terminal picker.',
  },
  {
    value: 'eol', label: 'End of life', tone: 'warning',
    description: 'Hidden from new bundles and deals. Existing bundles, prices and deployed terminals keep working. Requires a replacement device.',
  },
  {
    value: 'discontinued', label: 'Discontinued', tone: 'danger',
    description: 'Fully retired and no longer supplied. Requires a replacement device.',
  },
];

/**
 * Bundle lifecycle — the SAME shape as a device's, so a kit and the things in
 * it are not described by two different vocabularies.
 *
 * Draft is not offered here either: it is what a kit is until somebody moves
 * it on, and it is the only state that can be deleted outright, so a bundle
 * that IS one shows `BUNDLE_DRAFT_OPTION` locked rather than letting the bar
 * put a kit back into a state whose only extra power is deletion.
 */
export const BUNDLE_STATUS_OPTIONS: StatusOption[] = [
  {
    value: 'pilot', label: 'Pilot', tone: 'info',
    description: 'In trial with selected tenants. Real and deployable — every part must be live and compatible, exactly as for Active.',
  },
  {
    value: 'active', label: 'Active', tone: 'positive',
    description: 'Sellable. Every part must be Active and compatible with the terminal in the kit.',
  },
  {
    value: 'eol', label: 'Deactivated', tone: 'warning',
    description: 'Withdrawn from new deals. Deals already offering this kit keep working, and nothing already sold changes.',
  },
];

/** Shown ONLY when the kit is still a draft, and locked — see above. */
export const BUNDLE_DRAFT_OPTION: StatusOption = {
  value: 'draft', label: 'Draft', tone: 'neutral',
  description: 'Work in progress. May contain draft or incompatible parts, is not offered on any deal, and is the only state that can be deleted outright. Pick Pilot or Active to move it on.',
  lockedReason: 'Pick Pilot or Active to move this kit on. A kit cannot be put back into draft.',
};
